import PropTypes from "prop-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { formatError } from "helpers/formatError";
import useSwoogoRegistrantLeads, {
  SWOOGO_REGISTRANT_LEADS_QUERY_KEY,
} from "routes/leads/swoogo_registrants/useSwoogoRegistrantLeads";
import ChangeLeadStatusModal from "routes/leads/table/ChangeLeadStatusModal";
import LeadService from "services/Lead";
import MetaLeadsEventService, {
  META_LEADS_EVENTS_QUERY_KEY,
} from "services/MetaLeadsEvent";

/**
 * Sets the Meta status on the CRM lead of each given registrant. The backend
 * creates the CRM lead on first use and skips a registrant without an fbclid.
 */
const RegistrantLeadStatusModal = ({
  registrantIds,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const isSingle = registrantIds.length === 1;

  const { data: events = [] } = useQuery({
    queryKey: [META_LEADS_EVENTS_QUERY_KEY],
    queryFn: () => MetaLeadsEventService.list(),
    enabled: open,
  });

  // The picker keeps the status it opens with, so a single registrant's
  // current status is loaded before the picker is shown.
  const { leadsByRegistrant, isLoaded } = useSwoogoRegistrantLeads(
    isSingle ? registrantIds : []
  );
  const currentStatusId = isSingle
    ? leadsByRegistrant.get(registrantIds[0])?.lead?.status?.id
    : undefined;

  const mutation = useMutation({
    mutationFn: statusId =>
      LeadService.setSwoogoRegistrantLeadStatus(registrantIds, statusId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SWOOGO_REGISTRANT_LEADS_QUERY_KEY],
      });
      toast.success("Meta status updated");
      onSuccess?.();
    },
    onError: err =>
      toast.error(formatError(err, "Could not update the Meta status")),
  });

  return (
    <ChangeLeadStatusModal
      events={events}
      title="Set Meta Status"
      currentStatusId={currentStatusId}
      cancelVariant="ghost"
      open={open && (!isSingle || isLoaded)}
      onOpenChange={onOpenChange}
      onConfirm={statusId => mutation.mutateAsync(statusId)}
    />
  );
};

RegistrantLeadStatusModal.propTypes = {
  registrantIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default RegistrantLeadStatusModal;
